/**
 * Non-interactive display primitives: badges, panels, skeletons, empty and
 * error states, avatars, separators, progress.
 */
import { cva, type VariantProps } from 'class-variance-authority'
import { AlertTriangle, type LucideIcon } from 'lucide-react'
import { cn, initials } from '@/lib/utils'

// --- Badge ------------------------------------------------------------------

/**
 * Badges are square and mostly monochrome. A filled colour chip should mean
 * something a reader must act on — quarantine, a failed cell — not "this row
 * has a status". Everything else is a neutral outline.
 */
const badge = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-[var(--radius-xs)] border font-medium leading-none',
  {
    variants: {
      tone: {
        neutral:
          'border-[var(--color-border)] bg-[var(--color-surface-sunken)] text-[var(--color-fg-muted)]',
        primary: 'border-[var(--color-primary)]/35 bg-transparent text-[var(--color-primary)]',
        success: 'border-[var(--color-success)]/30 bg-transparent text-[var(--color-success)]',
        warning: 'border-[var(--color-warning)]/35 bg-transparent text-[var(--color-warning)]',
        danger: 'border-[var(--color-danger)]/40 bg-[var(--color-danger-bg)] text-[var(--color-danger)]',
        info: 'border-[var(--color-info)]/30 bg-transparent text-[var(--color-info)]',
        outline: 'border-[var(--color-border-strong)] bg-transparent text-[var(--color-fg-muted)]',
      },
      size: {
        sm: 'h-4.5 px-1.5 text-2xs',
        md: 'h-5 px-1.5 text-2xs',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'sm' },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badge> {}

export function Badge({ className, tone, size, ...props }: BadgeProps) {
  return <span className={cn(badge({ tone, size }), className)} {...props} />
}

/** A small coloured dot for status, paired with text — never colour alone. */
export function StatusDot({ tone, className }: { tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info'; className?: string }) {
  const colors = {
    neutral: 'bg-[var(--color-neutral-400)]',
    success: 'bg-[var(--color-success)]',
    warning: 'bg-[var(--color-warning)]',
    danger: 'bg-[var(--color-danger)]',
    info: 'bg-[var(--color-info)]',
  }
  return <span className={cn('inline-block size-1.5 shrink-0 rounded-full', colors[tone], className)} aria-hidden />
}

// --- Panel ------------------------------------------------------------------

export function Panel({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)]',
        className
      )}
      {...props}
    />
  )
}

/**
 * No description slot. Explanatory copy under a heading was the single biggest
 * source of clutter; where a caption is genuinely load-bearing it goes in
 * `meta`, right-aligned and small, so it reads as data rather than as prose.
 */
export function PanelHeader({
  title,
  meta,
  actions,
  className,
}: {
  title: React.ReactNode
  meta?: React.ReactNode
  actions?: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 border-b border-[var(--color-border)] px-3 py-2',
        className
      )}
    >
      <h2 className="truncate text-xs font-semibold tracking-tight text-[var(--color-fg)]">
        {title}
      </h2>
      <div className="flex shrink-0 items-center gap-2">
        {meta ? <span className="text-2xs text-[var(--color-fg-subtle)]">{meta}</span> : null}
        {actions}
      </div>
    </div>
  )
}

export function Separator({ className, vertical }: { className?: string; vertical?: boolean }) {
  return (
    <div
      role="separator"
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      className={cn(vertical ? 'w-px self-stretch' : 'h-px w-full', 'bg-[var(--color-border)]', className)}
    />
  )
}

// --- Loading & empty --------------------------------------------------------

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('shimmer rounded-[var(--radius-sm)] bg-[var(--color-surface-sunken)]', className)}
      aria-hidden
    />
  )
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon?: LucideIcon
  title: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 px-6 py-12 text-center', className)}>
      {Icon ? <Icon className="size-4 text-[var(--color-fg-subtle)]" /> : null}
      <p className="text-xs text-[var(--color-fg-muted)]">{title}</p>
      {description ? (
        <p className="max-w-sm text-2xs text-[var(--color-fg-subtle)]">{description}</p>
      ) : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

export function ErrorState({
  title,
  detail,
  action,
}: {
  title: React.ReactNode
  detail?: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <AlertTriangle className="size-4 text-[var(--color-danger)]" />
      <p className="text-xs font-medium text-[var(--color-fg)]">{title}</p>
      {detail ? (
        <p className="max-w-md font-mono text-2xs leading-relaxed break-words text-[var(--color-fg-subtle)]">
          {detail}
        </p>
      ) : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  )
}

// --- Avatar -----------------------------------------------------------------

/** Square, monochrome initials. Coloured circles read as a social product. */
export function Avatar({
  name,
  seed,
  size = 'md',
  className,
  src,
}: {
  name: string
  seed?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
  className?: string
  /**
   * A real photo, when there is one. Initials are the fallback rather than the
   * default: an account with a picture set should show it, and Clerk has one
   * for anyone who signed in through Google or Microsoft.
   */
  src?: string | null
}) {
  void seed
  const sizes = {
    xs: 'size-4.5 text-[9px]',
    sm: 'size-5.5 text-[10px]',
    md: 'size-7 text-2xs',
    lg: 'size-9 text-xs',
  }
  return (
    <span
      className={cn(
        'inline-grid shrink-0 place-items-center rounded-[var(--radius-xs)] font-medium select-none',
        'border border-[var(--color-border)] bg-[var(--color-surface-sunken)] text-[var(--color-fg-muted)]',
        sizes[size],
        className
      )}
      title={name}
      aria-hidden
    >
      {src ? (
        <img
          src={src}
          alt=""
          className="size-full rounded-[var(--radius-xs)] object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        initials(name)
      )}
    </span>
  )
}

// --- Progress ---------------------------------------------------------------

export function ProgressBar({
  value,
  max = 100,
  tone = 'primary',
  className,
  label,
}: {
  value: number
  max?: number
  tone?: 'primary' | 'success' | 'warning' | 'danger'
  className?: string
  label?: string
}) {
  const pct = max === 0 ? 0 : Math.min(100, Math.max(0, (value / max) * 100))
  const colors = {
    primary: 'bg-[var(--color-primary)]',
    success: 'bg-[var(--color-success)]',
    warning: 'bg-[var(--color-warning)]',
    danger: 'bg-[var(--color-danger)]',
  }
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-surface-sunken)]', className)}
    >
      <div
        className={cn('h-full rounded-full transition-[width] duration-300 ease-out', colors[tone])}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

/** Inline key/value rows used by approval previews and detail panels. */
export function DefinitionList({
  rows,
  className,
}: {
  rows: { label: React.ReactNode; value: React.ReactNode }[]
  className?: string
}) {
  return (
    <dl className={cn('grid grid-cols-[minmax(0,9rem)_1fr] gap-x-4 gap-y-1.5 text-xs', className)}>
      {rows.map((row, i) => (
        <div key={i} className="contents">
          <dt className="truncate text-[var(--color-fg-subtle)]">{row.label}</dt>
          <dd className="min-w-0 break-words text-[var(--color-fg)]">{row.value}</dd>
        </div>
      ))}
    </dl>
  )
}
